import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { PaymentMethod, PaymentType, PosTabStatus, TabDetailDto, TabItemDto } from '@klubovna/api-client';
import { TranslateModule } from '@ngx-translate/core';
import {
  GroupedItem,
  OrderTimelineEntry,
  OrderTimelineEntryType,
  TabDetailDialogData,
  TabDetailDialogResult,
  TabWriteoffDialogData,
  TabWriteoffDialogResult,
} from '@posModels';
import { PosOrdersApiService, PosTabsApiService } from '@posServices';
import { PosTabType } from '@posTypes';
import { getTabDisplayName } from '@posUtils';
import { NotificationService, SharedModule } from '@shared';
import { finalize } from 'rxjs';

import { TabPaymentDialogComponent } from '../tab-payment-form/tab-payment-dialog.component';
import { PaymentSubmitEvent, TabPaymentFormInputs } from '../tab-payment-form/tab-payment-form.model';
import { TabWriteoffDialogComponent } from '../tab-writeoff-dialog/tab-writeoff-dialog.component';

@Component({
  selector: 'app-tab-detail-dialog',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    MatDialogModule,
    TranslateModule,
    SharedModule,
  ],
  templateUrl: './tab-detail-dialog.component.html',
  styleUrl: './tab-detail-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabDetailDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<TabDetailDialogComponent, TabDetailDialogResult>);
  private data = inject<TabDetailDialogData>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private posTabsApi = inject(PosTabsApiService);
  private posOrdersApi = inject(PosOrdersApiService);
  private destroyRef = inject(DestroyRef);
  private notification = inject(NotificationService);

  readonly PosTabType = PosTabType;
  readonly PosTabStatus = PosTabStatus;
  readonly PaymentMethod = PaymentMethod;
  readonly OrderTimelineEntryType = OrderTimelineEntryType;

  tab = this.data.tab;
  allowWriteOff = this.data.allowWriteOff ?? false;
  detail = signal<TabDetailDto | null>(null);
  loading = signal(true);
  addingPayment = signal(false);
  itemsSectionExpanded = signal(false);
  historySectionExpanded = signal(false);
  duplicateConfirmOrder = signal<{ items: TabItemDto[]; totalCents: number } | null>(null);
  duplicating = signal(false);

  groupedItems = computed((): GroupedItem[] => {
    const d = this.detail();
    if (!d || !d.items) return [];

    const grouped = new Map<string, GroupedItem>();

    d.items.forEach(item => {
      const existing = grouped.get(item.productId);
      if (existing) {
        existing.totalQty += item.qty;
        existing.writtenOffQty += item.writtenOffQty ?? 0;
        existing.availableForWriteOffQty += item.availableForWriteOffQty ?? 0;
        if (item.paymentType === PaymentType.Money) {
          existing.moneyQty += item.qty;
          existing.moneyTotalCents += item.lineTotalCents;
          if (item.unitPoints > 0) {
            existing.pointsEligibleQty += item.qty;
          }
        } else {
          existing.pointsQty += item.qty;
          existing.pointsTotalPoints += item.unitPoints * item.qty;
        }
      } else {
        grouped.set(item.productId, {
          productId: item.productId,
          name: item.name,
          totalQty: item.qty,
          unitPriceCents: item.unitPriceCents,
          unitPoints: item.unitPoints,
          moneyQty: item.paymentType === PaymentType.Money ? item.qty : 0,
          pointsQty: item.paymentType === PaymentType.Points ? item.qty : 0,
          moneyTotalCents: item.paymentType === PaymentType.Money ? item.lineTotalCents : 0,
          pointsTotalPoints: item.paymentType === PaymentType.Points ? item.unitPoints * item.qty : 0,
          pointsEligibleQty: item.paymentType === PaymentType.Money && item.unitPoints > 0 ? item.qty : 0,
          writtenOffQty: item.writtenOffQty ?? 0,
          availableForWriteOffQty: item.availableForWriteOffQty ?? 0,
        });
      }
    });

    return Array.from(grouped.values());
  });

  ordersTimeline = computed((): OrderTimelineEntry[] => {
    const d = this.detail();
    if (!d) return [];

    const entries: OrderTimelineEntry[] = [];

    const orderGroups = new Map<string, TabItemDto[]>();
    d.items.forEach(item => {
      const existing = orderGroups.get(item.orderId);
      if (existing) {
        existing.push(item);
      } else {
        orderGroups.set(item.orderId, [ item ]);
      }
    });

    orderGroups.forEach((items, orderId) => {
      const totalCents = items.reduce((sum, item) => sum + item.lineTotalCents, 0);
      const earliestCreatedAt = items.reduce((earliest, item) =>
          item.createdAt < earliest ? item.createdAt : earliest,
        items[0].createdAt);

      entries.push({
        type: OrderTimelineEntryType.Order,
        orderId,
        createdAt: earliestCreatedAt,
        items,
        totalCents,
      });
    });

    d.payments.forEach(payment => {
      entries.push({
        type: OrderTimelineEntryType.Payment,
        createdAt: payment.createdAt,
        paymentAmountCents: payment.amountCents ?? undefined,
        paymentPoints: payment.pointsAmount ?? undefined,
        paymentMethod: payment.method ?? undefined,
      });
    });

    d.writeOffs?.forEach(writeoff => {
      entries.push({
        type: OrderTimelineEntryType.Writeoff,
        createdAt: writeoff.createdAt,
        writeoffAmountCents: writeoff.amountCents,
        writeoffReason: writeoff.reason,
        writeoffIsFullWriteOff: writeoff.isFullWriteOff,
      });
    });

    return entries.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  });

  unpaidGroupedItemsWithPoints = computed(() => {
    return this.groupedItems().filter(item => item.unitPoints > 0 && item.pointsEligibleQty > 0);
  });

  canPayWithPoints = computed(() => {
    const d = this.detail();
    if (!d) return false;
    if (this.tab.type !== PosTabType.User) return false;
    return d.userPointBalance !== undefined && d.userPointBalance !== null;
  });

  hasAnyBalance = computed(() => {
    const d = this.detail();
    return d ? (d.orderBalanceCents > 0 || d.orderBalancePoints > 0) : false;
  });

  hasPointsBalance = computed(() => {
    const d = this.detail();
    return d ? d.orderBalancePoints > 0 : false;
  });

  totalOrderPoints = computed(() => {
    return this.detail()?.orderTotalPoints ?? 0;
  });

  hasItemsWithPoints = computed(() => {
    const d = this.detail();
    if (!d) return false;
    return d.items.some(item => item.unitPoints > 0);
  });

  paymentFormData = computed((): TabPaymentFormInputs | null => {
    const d = this.detail();
    if (!d) return null;

    return {
      orderBalanceCents: d.orderBalanceCents,
      unpaidItemsWithPoints: this.unpaidGroupedItemsWithPoints(),
      userPointBalance: d.userPointBalance,
      canPayWithPoints: this.canPayWithPoints(),
      hasAnyBalance: this.hasAnyBalance(),
    };
  });

  ngOnInit(): void {
    this.loadDetail();
  }

  loadDetail(): void {
    this.loading.set(true);
    this.posTabsApi.getDetail({ id: this.tab.id })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (detail) => {
          this.detail.set(detail);
          if (detail.status === PosTabStatus.Closed) {
            this.dialogRef.close({ closed: true });
          }
        },
        error: () => this.notification.error('pos.tabs.detail_error'),
      });
  }

  showAddPayment(): void {
    const formData = this.paymentFormData();
    if (!formData) return;

    this.dialog.open<TabPaymentDialogComponent, TabPaymentFormInputs, PaymentSubmitEvent>(
      TabPaymentDialogComponent,
      {
        data: formData,
        panelClass: 'custom-dialog-panel',
        maxWidth: '95vw',
        width: '450px',
      },
    ).afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      if (result) {
        this.onPaymentSubmit(result);
      }
    });
  }

  onPaymentSubmit(event: PaymentSubmitEvent): void {
    if (event.type === PaymentType.Money) {
      this.submitMoneyPayment(event.amountCents, event.method);
    } else {
      this.submitPointsPayment(event.selectedByProduct);
    }
  }

  private submitMoneyPayment(amountCents: number, method: PaymentMethod): void {
    if (amountCents <= 0) return;

    this.addingPayment.set(true);
    this.posTabsApi.addPayment({
      id: this.tab.id,
      body: {
        paymentType: PaymentType.Money,
        amountCents,
        method,
      },
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.addingPayment.set(false)),
      )
      .subscribe({
        next: (updatedDetail) => this.handlePaymentSuccess(updatedDetail),
        error: () => this.notification.error('pos.tabs.payment_error'),
      });
  }

  private submitPointsPayment(selectedByProduct: Map<string, number>): void {
    const itemIds = this.resolveItemIds(selectedByProduct);
    if (itemIds.length === 0) return;

    this.addingPayment.set(true);
    this.posTabsApi.payItemsWithPoints({
      id: this.tab.id,
      body: { itemIds },
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.addingPayment.set(false)),
      )
      .subscribe({
        next: (updatedDetail) => this.handlePaymentSuccess(updatedDetail),
        error: () => this.notification.error('pos.tabs.payment_error'),
      });
  }

  private resolveItemIds(selectedByProduct: Map<string, number>): string[] {
    const d = this.detail();
    if (!d || !d.items) return [];

    const selectedIds: string[] = [];

    selectedByProduct.forEach((selectedQty, productId) => {
      if (selectedQty <= 0) return;

      const unpaidItems = d.items.filter(
        item => item.productId === productId &&
          item.paymentType === PaymentType.Money &&
          item.unitPoints > 0,
      );

      let remainingQty = selectedQty;
      unpaidItems.forEach(item => {
        if (remainingQty <= 0) return;
        selectedIds.push(item.id);
        remainingQty -= item.qty <= remainingQty ? item.qty : remainingQty;
      });
    });

    return selectedIds;
  }

  private handlePaymentSuccess(updatedDetail: TabDetailDto): void {
    this.detail.set(updatedDetail);
    this.notification.success('pos.tabs.payment_added');
    if (
      updatedDetail.status === PosTabStatus.Closed ||
      (updatedDetail.orderBalanceCents <= 0 && updatedDetail.orderBalancePoints <= 0)
    ) {
      this.notification.success('pos.tabs.auto_closed');
      this.dialogRef.close({ closed: true });
    }
  }

  getDisplayName(): string {
    return getTabDisplayName(this.tab);
  }

  duplicateOrder(items: TabItemDto[], totalCents: number): void {
    this.duplicateConfirmOrder.set({ items, totalCents });
  }

  cancelDuplicate(): void {
    this.duplicateConfirmOrder.set(null);
  }

  confirmDuplicate(): void {
    const order = this.duplicateConfirmOrder();
    if (!order) return;

    this.duplicating.set(true);
    this.posOrdersApi.createOrder({
      tabId: this.tab.id,
      items: order.items.map(item => ({
        productId: item.productId,
        qty: item.qty,
        paymentType: item.paymentType,
      })),
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.duplicating.set(false)),
    ).subscribe({
      next: () => {
        this.notification.success('pos.tabs.duplicate_success');
        this.duplicateConfirmOrder.set(null);
        this.loadDetail();
      },
      error: () => {
        this.notification.error('pos.tabs.duplicate_error');
      },
    });
  }

  toggleItemsSection(): void {
    this.itemsSectionExpanded.update(v => !v);
  }

  toggleHistorySection(): void {
    this.historySectionExpanded.update(v => !v);
  }

  openWriteoffDialog(): void {
    const d = this.detail();
    if (!d || d.status !== PosTabStatus.Open) return;

    const writeOffableItems = d.items.filter(item => item.availableForWriteOffQty > 0);
    if (writeOffableItems.length === 0) return;

    const dialogData: TabWriteoffDialogData = {
      tabId: this.tab.id,
      tabName: this.getDisplayName(),
      items: writeOffableItems,
    };

    this.dialog.open<TabWriteoffDialogComponent, TabWriteoffDialogData, TabWriteoffDialogResult>(
      TabWriteoffDialogComponent,
      {
        data: dialogData,
        panelClass: 'custom-dialog-panel',
        maxWidth: '95vw',
        width: '500px',
      },
    ).afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      if (result?.confirmed && result.items && result.reason) {
        this.loading.set(true);
        this.posTabsApi.writeOffTab({
          id: this.tab.id,
          body: {
            items: result.items,
            reason: result.reason,
          },
        }).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.loading.set(false)),
        ).subscribe({
          next: (updatedDetail) => {
            this.detail.set(updatedDetail);
            this.notification.success('pos.tabs.writeoff.success');
            if (updatedDetail.status === PosTabStatus.Closed) {
              this.dialogRef.close({ closed: true });
            }
          },
          error: () => {
            this.notification.error('pos.tabs.writeoff.error');
          },
        });
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
